import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  Badge,
  Button,
  LoadingState,
  ErrorState,
  EmptyState,
} from "@/components/design-system";
import { formatApiError } from "@/lib/api";
import {
  adminLedger,
  startPayoutRun,
  listPayoutRuns,
  payoutRunTransfers,
  formatCents,
} from "@/lib/payments";

const cn = (...xs) => xs.filter(Boolean).join(" ");
const TABS = [
  { id: "", label: "All" },
  { id: "accrued", label: "Accrued" },
  { id: "paid", label: "Paid" },
];

const STATUS_BADGE = {
  accrued: { variant: "neutral", label: "Accrued" },
  paid: { variant: "active", label: "Paid" },
  paid_founder: { variant: "founder", label: "Founder paid" },
};

function Stat({ label, value, testId, tone = "navy" }) {
  const tones = {
    navy: "text-[#0B1F3A]",
    green: "text-[#7ED957]",
    blue: "text-[#2D7FF9]",
    cyan: "text-[#20C7E8]",
  };
  return (
    <Card className="!p-4">
      <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">{label}</div>
      <div data-testid={testId} className={`mt-1 font-[Montserrat] font-extrabold text-2xl ${tones[tone]}`}>{value}</div>
    </Card>
  );
}

export default function AdminPayouts() {
  const [tab, setTab] = useState("");
  const [state, setState] = useState({ status: "loading", err: "" });
  const [ledger, setLedger] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [runTransfers, setRunTransfers] = useState([]);

  const load = async () => {
    try {
      const [l, r] = await Promise.all([adminLedger(tab || undefined), listPayoutRuns()]);
      setLedger(l);
      setRuns(r.items || []);
      setState({ status: "ready", err: "" });
    } catch (err) {
      setState({ status: "error", err: formatApiError(err) });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const runPayouts = async () => {
    if (!confirm("Run the monthly payout sweep now? This will transfer every accrued balance to connected accounts.")) return;
    setRunning(true);
    try {
      const { run } = await startPayoutRun();
      toast.success(`Payout run ${run.run_id.slice(-8)} — paid ${formatCents(run.total_paid_cents)} across ${run.paid_recipients} recipients.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  const showRun = async (runId) => {
    if (expandedRun === runId) { setExpandedRun(null); return; }
    setExpandedRun(runId);
    try {
      const res = await payoutRunTransfers(runId);
      setRunTransfers(res.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (state.status === "loading") return <LoadingState label="Loading admin payouts…" />;
  if (state.status === "error") return <ErrorState detail={state.err} />;

  const c = ledger?.counters || {};

  return (
    <div data-testid="admin-payouts-page" className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Admin · Payouts</p>
          <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
            Revenue ledger & payout runs
          </h1>
          <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
            Sweep every accrued balance into a monthly payout. Skipped recipients need to finish
            Stripe Connect onboarding — they'll be swept next run automatically.
          </p>
        </div>
        <Button variant="primary" onClick={runPayouts} disabled={running || (c.accrued || 0) === 0}
                data-testid="admin-run-payouts">
          {running ? "Running…" : "Run monthly payouts"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Accrued (rows)" value={c.accrued || 0} tone="cyan" testId="admin-c-accrued" />
        <Stat label="Paid (rows)" value={c.paid || 0} tone="green" testId="admin-c-paid" />
        <Stat label="Gross total" value={formatCents(c.gross_total_cents || 0)} tone="navy" testId="admin-c-gross" />
        <Stat label="Platform take" value={formatCents(c.platform_total_cents || 0)} tone="blue" testId="admin-c-platform" />
      </div>

      <Card>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Ledger</h2>
          <div className="flex gap-1 rounded-full bg-[#E8F1FF] p-1" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id || "all"}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id || "all"}`}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors",
                  t.id === tab ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70 hover:text-[#0B1F3A]"
                )}
              >{t.label}</button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          {(!ledger?.items || ledger.items.length === 0) ? (
            <EmptyState title="No ledger rows" description="Rows appear here after a founder subscribes." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-ledger-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 border-b border-[#0B1F3A]/10">
                    <th className="py-2 pr-3">Founder</th>
                    <th className="py-2 pr-3">Ambassador</th>
                    <th className="py-2 pr-3">Gross</th>
                    <th className="py-2 pr-3">85% F</th>
                    <th className="py-2 pr-3">12.5% A</th>
                    <th className="py-2 pr-3">Period</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.items.map((r) => {
                    const meta = STATUS_BADGE[r.status] || STATUS_BADGE.accrued;
                    return (
                      <tr key={r.id} className="border-b border-[#0B1F3A]/5">
                        <td className="py-2 pr-3 text-[#0B1F3A]/85">
                          {r.founder?.name || r.founder?.email || "—"}
                        </td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/85">
                          {r.ambassador ? (r.ambassador.name || r.ambassador.email) : <span className="text-[#0B1F3A]/40">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/85">{formatCents(r.gross_cents, r.currency)}</td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/85">{formatCents(r.founder_cents, r.currency)}</td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/85">{formatCents(r.ambassador_cents, r.currency)}</td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/70">
                          {new Date(r.period_start).toLocaleDateString()} → {new Date(r.period_end).toLocaleDateString()}
                        </td>
                        <td className="py-2 pr-3"><Badge variant={meta.variant}>{meta.label}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Payout runs</h2>
        <p className="text-sm text-[#0B1F3A]/70 mt-1">Chronological log — click a run to see individual transfers.</p>
        <div className="mt-4">
          {runs.length === 0 ? (
            <EmptyState title="No payout runs yet" description="Kick one off with 'Run monthly payouts'." />
          ) : (
            <div className="space-y-2" data-testid="admin-runs-list">
              {runs.map((r) => (
                <div key={r.run_id} className="rounded-lg border border-[#0B1F3A]/10 bg-white">
                  <button
                    onClick={() => showRun(r.run_id)}
                    className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-[#E8F1FF]"
                    data-testid={`admin-run-${r.run_id.slice(-8)}`}
                  >
                    <div>
                      <div className="text-sm font-bold text-[#0B1F3A]">
                        {new Date(r.created_at).toLocaleString()}
                      </div>
                      <div className="text-xs text-[#0B1F3A]/60 font-mono">{r.run_id}</div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-[#0B1F3A]/70">{r.paid_recipients} paid</span>
                      {r.skipped_recipients > 0 && (
                        <span className="text-[#0B1F3A]/50">· {r.skipped_recipients} skipped</span>
                      )}
                      <span className="font-bold text-[#7ED957]">{formatCents(r.total_paid_cents)}</span>
                    </div>
                  </button>
                  {expandedRun === r.run_id && (
                    <div className="border-t border-[#0B1F3A]/10 p-4 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-widest text-[#0B1F3A]/60">
                            <th className="py-1 pr-3">Recipient user id</th>
                            <th className="py-1 pr-3">Role</th>
                            <th className="py-1 pr-3">Connect acct</th>
                            <th className="py-1 pr-3">Amount</th>
                            <th className="py-1 pr-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {runTransfers.map((t) => (
                            <tr key={t._id} className="border-t border-[#0B1F3A]/5">
                              <td className="py-1 pr-3 font-mono text-[#0B1F3A]/70">{t.user_id}</td>
                              <td className="py-1 pr-3">{t.role}</td>
                              <td className="py-1 pr-3 font-mono text-[#0B1F3A]/60">{t.connect_account_id || "—"}</td>
                              <td className="py-1 pr-3 font-bold text-[#0B1F3A]">{formatCents(t.amount_cents)}</td>
                              <td className="py-1 pr-3">
                                <Badge variant={t.status === "paid" ? "active" : "admin"}>
                                  {t.status === "paid" ? "Paid" : "Skipped"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
